import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Headphones, Film, Volume2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Federation Video Hub — self-verified replacement for the YouTube
 * iframe strip.  Videos are surfaced as tiles that link out to their
 * canonical source rather than embedding a third-party player.
 *
 * Right pane is the same Audio Library that used to live in MediaStrip.
 */
const FEDERATION_VIDEO_LINKS = [
  {
    id: 'nextxus-playlist',
    title: 'NextXus Federation Playlist',
    subtitle: 'Architect archive · long-form',
    description:
      'The Architect\u2019s curated playlist covering Federation origins, the 112 Sacred Directives, and Xavier\u2019s teaching arc. Opens in a new tab.',
    url: 'https://www.youtube.com/playlist?list=PLM9vg3bOgbD_80B36YRXgKBKZ2Xlr0MlC',
    accent: 'purple',
  },
  {
    id: 'nextxus-federation-hub',
    title: 'Federation Hub',
    subtitle: 'Cross-node signal',
    description:
      'Live updates and Federation-wide broadcasts from next-xus.com. Cross-pillar announcements land here first.',
    url: 'https://next-xus.com',
    accent: 'amber',
  },
];

export default function FederationVideoHub() {
  const [tracks, setTracks] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const listResp = await fetch(`${API_URL}/api/library/documents`);
        const listData = await listResp.json();
        const docs = Array.isArray(listData) ? listData : listData?.documents || [];
        const audioDocs = docs.filter((d) => d.has_audio);
        const detailed = await Promise.all(
          audioDocs.map(async (d) => {
            try {
              const r = await fetch(`${API_URL}/api/library/documents/${d.doc_id}`);
              const data = await r.json();
              return {
                doc_id: d.doc_id,
                title: d.title,
                audioTitle: data.audio_title || data.title || d.title,
                audioUrl: data.audio_url,
              };
            } catch {
              return null;
            }
          }),
        );
        if (!cancelled) setTracks(detailed.filter((t) => t && t.audioUrl));
      } catch {
        if (!cancelled) setTracks([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const active = tracks[activeIdx];

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };

  const selectTrack = (i) => {
    setActiveIdx(i);
    setTimeout(() => {
      const a = audioRef.current;
      if (!a) return;
      a.load();
      a.play().catch(() => {});
    }, 80);
  };

  return (
    <section
      data-testid="federation-video-hub"
      aria-label="Federation Video Hub — self-verified video links and audio library"
      className="bg-black border-y border-purple-500/20"
    >
      <div className="container mx-auto px-4 py-14">
        <div className="text-center max-w-2xl mx-auto mb-9">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-purple-300/85 mb-2">
            Watch · Listen · Learn
          </div>
          <h2 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight">
            The <span className="text-purple-300">Federation Video Hub</span>
          </h2>
          <p className="mt-3 text-xs md:text-sm text-gray-400">
            Self-verified · no embedded third-party players
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* ===== LEFT: Federation Video Hub ===== */}
          <article
            data-testid="federation-video-panel"
            className="rounded-2xl overflow-hidden border-2 border-purple-500/30 bg-gradient-to-br from-purple-950/30 via-gray-900 to-black"
            style={{ boxShadow: '0 0 30px rgba(168,85,247,0.12)' }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-purple-500/20">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 border border-purple-400/40">
                  <Film className="w-4 h-4 text-purple-300" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300/85">
                    Federation Video Hub
                  </div>
                  <div className="text-sm font-bold text-white leading-tight">
                    Xavier · The Teacher Wing
                  </div>
                </div>
              </div>
              <span
                className="text-[10px] font-mono uppercase tracking-[0.16em] text-purple-300 border border-purple-400/30 rounded-full px-3 py-1"
                data-testid="federation-video-verified-badge"
              >
                Verified
              </span>
            </header>

            {FEDERATION_VIDEO_LINKS.length === 0 ? (
              <div className="p-8 text-center">
                <p
                  className="text-sm text-gray-400"
                  data-testid="federation-video-empty"
                >
                  Federation Video Hub — Content being migrated from legacy
                  platforms.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-purple-500/10">
                {FEDERATION_VIDEO_LINKS.map((v) => (
                  <li key={v.id}>
                    <a
                      href={v.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`federation-video-tile-${v.id}`}
                      className="group flex items-start gap-4 px-4 py-4 hover:bg-purple-500/5 transition-colors"
                    >
                      <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-purple-500/15 border border-purple-400/30 flex-shrink-0 group-hover:bg-purple-500/25 transition-colors">
                        <Film className="w-5 h-5 text-purple-200" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300/75 mb-1">
                          {v.subtitle}
                        </span>
                        <span className="block text-sm font-semibold text-white group-hover:text-purple-200 transition-colors">
                          {v.title}
                        </span>
                        <span className="block text-xs text-gray-400 mt-1 leading-relaxed">
                          {v.description}
                        </span>
                      </span>
                      <ExternalLink className="w-4 h-4 text-purple-300/70 flex-shrink-0 mt-1 group-hover:text-purple-200 transition-colors" />
                    </a>
                  </li>
                ))}
                <li className="px-4 py-3 border-t border-purple-500/15">
                  <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-gray-500 text-center">
                    No third-party iframes · your privacy stays with you
                  </p>
                </li>
              </ul>
            )}
          </article>

          {/* ===== RIGHT: Audio Library (unchanged) ===== */}
          <article
            data-testid="media-audio"
            className="rounded-2xl overflow-hidden border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-950/30 via-gray-900 to-black flex flex-col"
            style={{ boxShadow: '0 0 30px rgba(34,211,238,0.12)' }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-cyan-500/20">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-400/40">
                  <Headphones className="w-4 h-4 text-cyan-300" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300/85">
                    Audio Library · Podcasts & Lectures
                  </div>
                  <div className="text-sm font-bold text-white leading-tight">
                    Federation Audio
                  </div>
                </div>
              </div>
              <a
                href="/library"
                data-testid="media-audio-library-link"
                className="text-[10px] font-mono uppercase tracking-[0.16em] text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 rounded-full px-3 py-1 transition-colors"
              >
                Library
              </a>
            </header>

            <div className="p-5 border-b border-cyan-500/15">
              {active ? (
                <>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-300/80 mb-1">
                    Now playing
                  </div>
                  <div
                    className="text-sm md:text-base font-semibold text-white leading-snug mb-3"
                    data-testid="media-audio-now-playing"
                    title={active.audioTitle}
                  >
                    {active.audioTitle}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={toggle}
                      data-testid="media-audio-play-toggle"
                      aria-label={playing ? 'Pause' : 'Play'}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black transition-colors shadow-lg shadow-cyan-500/30"
                    >
                      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <audio
                      ref={audioRef}
                      key={active.audioUrl}
                      src={active.audioUrl}
                      preload="metadata"
                      controls
                      onPlay={() => setPlaying(true)}
                      onPause={() => setPlaying(false)}
                      onEnded={() => setPlaying(false)}
                      data-testid="media-audio-player"
                      className="flex-1 min-w-0"
                      style={{ accentColor: '#22d3ee', maxWidth: '100%' }}
                    >
                      Audio not supported.
                    </audio>
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-400 inline-flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-cyan-400/70" />
                  No audio companions inscribed yet — drop an <code className="text-cyan-300">audio_url</code> on a Library doc and it&apos;ll appear here automatically.
                </div>
              )}
            </div>

            <ul
              data-testid="media-audio-tracklist"
              className="flex-1 overflow-y-auto max-h-[260px] divide-y divide-cyan-500/10"
            >
              {tracks.map((t, i) => {
                const isActive = i === activeIdx;
                return (
                  <li key={t.doc_id}>
                    <button
                      type="button"
                      onClick={() => selectTrack(i)}
                      data-testid={`media-audio-track-${i + 1}`}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                        isActive ? 'bg-cyan-500/12' : 'hover:bg-white/5'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-[11px] font-mono font-bold flex-shrink-0 ${
                          isActive
                            ? 'bg-cyan-400 text-black'
                            : 'bg-cyan-400/15 text-cyan-200 border border-cyan-400/30'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{t.audioTitle}</span>
                        <span className="block text-[10px] font-mono text-gray-400 uppercase tracking-[0.16em] truncate">
                          {t.doc_id}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {tracks.length === 0 && (
                <li className="px-4 py-6 text-center text-xs text-gray-500">
                  No audio tracks yet — the list will populate automatically.
                </li>
              )}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}
